import { useEffect, useRef, useState } from "react";
import { Page } from "../../App";
import USMapHero from "../../components/USMapHero";
import "./LandingUS.css";

const STATS = [
  { value: "8B", label: "Trips / year", sub: "Inter-city domestic trips above 100NM" },
  { value: "800", label: "Routes closed", sub: "Regional air routes closed since year 2000" },
  { value: "15", label: "Heartland cities", sub: "Important industrial and cultural communities needing better air connections" },
];

const TICKER_ITEMS = [
  "8B inter-city domestic trips per year in the US",
  "75% of trips are under 400NM, only 4% by air",
  "800 regional air routes closed since 2000",
  "90% of air mobility flows touch an L hub",
  "15 focus cities across Middle America",
  "1.5B trips/year mobility market from focus cities",
  "x6 higher indirect-routing probability from focus cities",
  "+2h extra journey time vs a direct flight",
  "300 turboprops can complement RJs in the US air system",
];

export default function LandingUS({ navigate }: { navigate: (p: Page) => void }) {
  const [loaded, setLoaded] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  const targets = STATS.map((s) => parseFloat(s.value.replace(/[^0-9.]/g, "")) || 0);
  const [counts, setCounts] = useState<number[]>([...targets]);
  const animFrameId = useRef<number | null>(null);

  const formatValue = (index: number, num: number) => {
    const raw = STATS[index].value;
    const prefix = raw.match(/^[^0-9]*/)?.[0] || "";
    const suffix = raw.match(/[^0-9]*$/)?.[0] || "";
    return `${prefix}${num}${suffix}`;
  };

  const startAnimation = (index: number) => {
    if (animFrameId.current) {
      cancelAnimationFrame(animFrameId.current);
      animFrameId.current = null;
    }

    const target = targets[index];
    if (target === 0) return;

    setCounts((prev) => {
      const newCounts = [...prev];
      newCounts[index] = 0;
      return newCounts;
    });

    const duration = 1800;
    const startTime = performance.now();

    const update = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const currentVal = Math.round(target * eased);

      setCounts((prev) => {
        const newCounts = [...prev];
        newCounts[index] = currentVal;
        return newCounts;
      });

      if (progress < 1) {
        animFrameId.current = requestAnimationFrame(update);
      } else {
        setCounts((prev) => {
          const newCounts = [...prev];
          newCounts[index] = target;
          return newCounts;
        });
        animFrameId.current = null;
      }
    };

    animFrameId.current = requestAnimationFrame(update);
  };



useEffect(() => {
  const timer = setTimeout(() => setLoaded(true), 100);

  return () => clearTimeout(timer);
}, []);
  return (
    <div className="lp">
      <div className="lp-aurora"><span /></div>
      <div className="lp-bg-photo">
        <img src="/photos/ATR-158496_BD.jpg" alt="" />
        <div className="lp-bg-overlay" />
      </div>
      <div className="lp-grain" />

      <div className="lp-corner lp-corner--tl" />
      <div className="lp-corner lp-corner--br" />

      <div className="lp-hero">
        <div className={`lp-left ${loaded ? "lp-left--in" : ""}`}>
          <h1 className="lp-title">
            <span
              className="lp-title-line"
style={{
  "--line-delay": "0.15s",
  color: "#009A84",
  fontSize: "0.90em",
  filter: "drop-shadow(0 0 30px rgba(0, 154, 132, 0.3))",
} as React.CSSProperties}
            >
              <span className="lp-title-hl"> Reconnecting "America's Heartland"</span>
            </span>
          </h1>

          <div className="lp-desc-wrapper">
            <p className="lp-desc">
            The United States has built one of the world's most extensive transportation networks. Yet for thousands of communities, convenient air service remains increasingly out of reach.
        </p>
        <p className="lp-desc">
          Since 2000, hundreds of regional air connections have disappeared.
        </p>
            <p className="lp-desc">
              Yet when looking at the overall need for inter-city travel in the US, <strong>75% of trips</strong> by all modes of transport are regional, being under 400 NM. 
            </p>
<div className="lp-stats">
            {STATS.map((s, i) => (
              <div
                key={i}
                className={`lp-stat ${activeIdx === i ? "lp-stat--on" : ""}`}
                onMouseEnter={() => setActiveIdx(i)}
              >
                <div className="lp-stat-bar" />
                <div className="lp-stat-content">
                  <div className="lp-stat-line">
                    <span className="lp-stat-val">{formatValue(i, counts[i])}</span>
                    <span className="lp-stat-label">{s.label}</span>
                  </div>
                  <span className="lp-stat-sub">{s.sub}</span>
                </div>
              </div>
            ))}
          </div>
            <p className="lp-desc">
              Explore this interactive experience to discover how <span className="highlight-yellow">ATR's MobilityMonitor </span>  
              identifies 15 opportunities in America's Heartland and how turboprops can revive air connectivity by bringing people back to the skies.
            </p>
          </div>

          

          <div className="lp-ctas">
            <button className="lp-cta-main" onClick={() => navigate("usStory")}>
              <span>Start Exploring</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        <div className={`lp-right ${loaded ? "lp-right--in" : ""}`}>
          <USMapHero />
        </div>
      </div>

      <div className="lp-ticker">
        <div className="lp-ticker-track">
          {[...TICKER_ITEMS, ...TICKER_ITEMS].map((item, i) => (
            <span key={i} className="lp-ticker-item">
              <span className="lp-ticker-dot" />{item}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
